import { PalletCrowdloanRewardsRewardInfo } from '@polkadot/types/lookup';
import { useApi } from 'hooks/useApi';
import { useApiCall } from 'hooks/useApiCall';
import { useEffect, useState } from 'react';
import { transformU32ToBigint } from 'utils/queries';

interface PayableTransform {
  claimedRewards: bigint;
  totalRewards: bigint;
}

interface CrowdloanRewards extends PayableTransform {
  claimableRewards: bigint;
}

function transformPayable({
  claimedReward,
  isEmpty,
  totalReward,
}: PalletCrowdloanRewardsRewardInfo): PayableTransform {
  return {
    claimedRewards: isEmpty ? 0n : claimedReward.toBigInt(),
    totalRewards: isEmpty ? 0n : totalReward.toBigInt(),
  };
}

function calcClaimableRewards(
  claimedRewards: bigint,
  endBlock: bigint,
  initBlock: bigint,
  relayLatestBlock: bigint,
  totalRewards: bigint,
): bigint {
  let claimable = 0n;

  if (endBlock > 0) {
    const compBlock = relayLatestBlock < endBlock ? relayLatestBlock : endBlock;
    const totalClaimable = (totalRewards * 10n * 7n) / 100n;

    claimable =
      (totalClaimable / (endBlock - initBlock)) * (compBlock - initBlock);
    claimable -= claimedRewards - totalRewards + totalClaimable;
    if (claimable < 0) claimable = 0n;
  }

  return claimable;
}

export function useCrowdloanRewards(
  address?: string,
): CrowdloanRewards | undefined {
  const api = useApi();
  const [rewards, setRewards] = useState<CrowdloanRewards>();

  const relayLatestBlock = useApiCall(
    api?.query.parachainSystem.lastRelayChainBlockNumber,
    [],
    transformU32ToBigint,
  );
  const initBlock = useApiCall(
    api?.query.crowdloanRewards.initRelayBlock,
    [],
    transformU32ToBigint,
  );
  const endBlock = useApiCall(
    api?.query.crowdloanRewards.endRelayBlock,
    [],
    transformU32ToBigint,
  );

  const payable = useApiCall(
    api?.query.crowdloanRewards.accountsPayable,
    [address],
    transformPayable,
  );

  useEffect(() => {
    if (!relayLatestBlock || !initBlock || !endBlock || !payable) return;

    setRewards({
      claimableRewards: calcClaimableRewards(
        payable.claimedRewards,
        endBlock,
        initBlock,
        relayLatestBlock,
        payable.totalRewards,
      ),
      claimedRewards: payable.claimedRewards,
      totalRewards: payable.totalRewards,
    });
  }, [relayLatestBlock, initBlock, endBlock, payable]);

  return rewards;
}
